import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/aevos/PageHero";
import { CheckCircle2, ArrowRight } from "lucide-react";

const plans = [
  {
    key: "online",
    name: "AEVOS Online",
    tagline: "Acompanhamento médico online.",
    desc: "Atendimento remoto para pacientes de qualquer região do Brasil.",
    includes_title: "Inclui",
    items: [
      "4 consultas online",
      "Análise de exames",
      "Estratégia personalizada",
      "Acompanhamento remoto por 4 meses",
    ],
    ideal: [],
    investment: { main: "R$ 1.980", alt: "ou 4× de R$ 495" },
  },
  {
    key: "performance",
    name: "AEVOS Performance",
    tagline: "O programa ideal para quem deseja emagrecer, melhorar a composição corporal e construir resultados sustentáveis.",
    desc: "Desenvolvido para pacientes que desejam acompanhamento médico estruturado através de uma equipe integrada.",
    includes_title: "Inclui",
    items: [
      "4 consultas médicas",
      "4 avaliações de composição corporal (Bioimpedância)",
      "1 avaliação metabólica (Calorimetria)",
      "Acompanhamento nutricional",
      "Acompanhamento físico personalizado",
      "Monitoramento de evolução por 4 meses",
    ],
    ideal: [
      "Emagrecimento",
      "Ganho de massa muscular",
      "Reorganização metabólica",
      "Mudança de hábitos",
      "Saúde e performance",
    ],
    investment: { main: "R$ 4.500", alt: "ou R$ 4.275 via PIX" },
  },
  {
    key: "elite",
    name: "AEVOS Elite",
    tagline: "O mais completo programa de acompanhamento da AEVOS.",
    desc: "Desenvolvido para pacientes que desejam máxima personalização, monitoramento avançado e acompanhamento premium.",
    includes_title: "Inclui",
    items: [
      "Tudo que está incluso no AEVOS Performance",
      "Monitoramento ampliado",
      "Prioridade de agenda",
      "Ajustes estratégicos contínuos",
      "Relatório executivo de evolução",
      "Comunicação facilitada com a equipe",
      "Acompanhamento médico mais próximo",
      "2 aplicações terapêuticas (venosas ou intramusculares), conforme indicação médica",
    ],
    ideal: ["Alta performance", "Otimização metabólica", "Longevidade", "Pacientes que desejam acompanhamento premium"],
    investment: { main: "R$ 5.500", alt: "ou R$ 5.225 via PIX" },
    featured: true,
  },
  {
    key: "365",
    name: "AEVOS 365",
    tagline: "Saúde, performance e longevidade durante todo o ano.",
    desc: "Programa anual para pacientes que desejam acompanhamento contínuo e planejamento estratégico de saúde.",
    includes_title: "Inclui",
    items: [
      "12 consultas médicas",
      "12 bioimpedâncias",
      "3 avaliações metabólicas (Calorimetria)",
      "Acompanhamento nutricional mensal",
      "Acompanhamento físico mensal",
      "Acompanhamento fisioterapêutico / massoterapêutico mensal",
      "Revisão anual estratégica de saúde",
      "Planejamento de metas anuais",
    ],
    ideal: ["Longevidade", "Controle contínuo de peso", "Performance sustentável", "Acompanhamento de longo prazo"],
    investment: { main: "Sob consulta", alt: null },
  },
];

const PlanCard = ({ p }) => {
  return (
    <div
      className={`p-8 flex flex-col ${p.featured ? "plan-featured bg-[#E6F8FF]" : "aevos-card"}`}
      data-testid={`plan-card-${p.key}`}
    >
      <h3 className="font-display font-bold text-2xl text-black">{p.name}</h3>
      <p className="font-display text-sm font-medium text-[#00C4FF] mt-2">{p.tagline}</p>
      <p className="mt-3 text-sm text-[#333] leading-relaxed">{p.desc}</p>

      <div className="mt-6">
        <p className="aevos-eyebrow">{p.includes_title}</p>
        <ul className="mt-3 space-y-2">
          {p.items.map((it) => (
            <li key={it} className="flex items-start gap-2 text-sm text-[#333]">
              <CheckCircle2 size={16} className="text-[#00C4FF] mt-0.5 flex-shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>

      {p.ideal.length > 0 && (
        <div className="mt-6">
          <p className="aevos-eyebrow">Ideal para</p>
          <ul className="mt-3 space-y-1.5">
            {p.ideal.map((it) => (
              <li key={it} className="text-sm text-[#333] flex items-center gap-2">
                <span className="text-[#00C4FF]">•</span>{it}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-auto pt-7">
        <div className="pt-5 border-t border-[#E0E0E0]">
          <p className="text-xs text-[#666] uppercase tracking-wider">Investimento</p>
          <p className="font-display font-bold text-3xl text-black mt-1">{p.investment.main}</p>
          {p.investment.alt && (
            <p className="text-xs text-[#666] mt-1">{p.investment.alt}</p>
          )}
        </div>

        <Link to="/contato" className="mt-6 inline-block w-full">
          <span className={`${p.featured ? "btn-primary" : "btn-ghost"} w-full justify-center`}>
            Falar com a clínica <ArrowRight size={16} />
          </span>
        </Link>
      </div>
    </div>
  );
};

const Planos = () => {
  return (
    <div data-testid="page-planos">
      <PageHero
        eyebrow="Planos & Investimento"
        title="Quatro caminhos. Uma filosofia clínica."
        subtitle="Acompanhamento integrado, mensurável e individualizado — escolha o programa AEVOS que faz sentido para o seu momento."
      />

      <section className="section-pad">
        <div className="aevos-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((p) => (
              <PlanCard key={p.key} p={p} />
            ))}
          </div>

          <div className="mt-12 p-6 md:p-8 border border-[#E0E0E0] bg-white text-sm text-[#333]">
            <p className="aevos-eyebrow mb-2">Importante</p>
            <p>
              Todos os programas AEVOS são personalizados após avaliação médica. A composição do
              acompanhamento pode variar conforme os objetivos, histórico clínico e necessidades
              individuais de cada paciente.
            </p>
          </div>

          <div className="mt-10 text-center max-w-3xl mx-auto">
            <p className="text-[#333] text-base md:text-lg italic">
              Toda transformação começa na origem. Assim como as moléculas formam sistemas complexos
              e inteligentes, a sua saúde é construída pela integração entre{" "}
              <span className="text-[#00C4FF] not-italic font-semibold">ciência, hábitos, estratégia e acompanhamento</span>.
            </p>
          </div>
        </div>
      </section>

      <section className="pt-20 md:pt-28 pb-10 md:pb-14 bg-black text-white text-center">
        <div className="aevos-container">
          <h2 className="font-display font-bold text-3xl md:text-5xl max-w-3xl mx-auto leading-[1.1] !text-white">
            Dúvidas? Vamos conversar antes de você decidir.
          </h2>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contato"><span className="btn-cyan">Agendar Consulta</span></Link>
            <a href="https://wa.me/5511941546532" target="_blank" rel="noreferrer"><span className="btn-ghost-on-dark">Falar no WhatsApp</span></a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Planos;
